(() => {
  if (customElements.get('dod-drinking-line')) return;
  class DrinkingLine extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.cards = [...this.querySelectorAll('[data-game]')];
      this.games = new Map(this.cards.map(card => [card.dataset.game, {
        id: card.dataset.game, title: card.dataset.title, handle: card.dataset.handle,
        variant: Number(card.dataset.variant), price: Number(card.dataset.price),
        available: card.dataset.available === 'true', card,
        images: JSON.parse(card.querySelector('[data-game-images]').textContent),
        url: card.querySelector('[data-game-url]').href
      }]));
      this.selected = new Set([this.dataset.first, this.dataset.second].filter(id => this.games.get(id)?.available));
      this.games.forEach(game => { if (this.selected.size < 2 && game.available) this.selected.add(game.id); });
      this.bundleSize = 3;
      this.gallery = this.querySelector('[data-gallery]');
      this.galleryRail = this.querySelector('[data-gallery-rail]');
      this.browser = this.querySelector('[data-browser]');
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.addEventListener('click', event => this.onClick(event), opts);
      this.galleryRail.addEventListener('scroll', () => this.trackImages(), { ...opts, passive: true });
      [this.gallery, this.browser].forEach(dialog => dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
      }, opts));
      this.buildBrowser();
      this.render();
    }
    disconnectedCallback() { this.abort?.abort(); this.ready = false; }
    money(value) { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(value / 100); }
    format(text, values) { return Object.entries(values).reduce((s, [key, value]) => s.replaceAll('[' + key + ']', value), text); }
    behavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    pack(game) { return game.card.querySelector('[data-pack-template]').content.cloneNode(true); }
    price(game) { return game.card.querySelector('[data-game-price]').cloneNode(true); }
    tier(count) { return count >= 4 ? 40 : count === 3 ? 30 : count === 2 ? 20 : 0; }
    discounted(games, percent) { return games.reduce((sum, game) => sum + game.price - Math.floor(game.price * percent / 100), 0); }
    setStatus(message) { this.querySelectorAll('[data-status],[data-modal-status]').forEach(node => { node.textContent = message; }); }
    toggle(id) {
      if (this.busy || !this.games.get(id)?.available) return;
      this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
      this.setStatus(''); this.render();
    }
    setBundleSize(size) {
      if (this.busy || ![2, 3, 4].includes(size)) return;
      this.bundleSize = size;
      this.renderOccasions();
      this.setStatus(this.format(this.copy.bundleSizeStatus, { count: size, percent: this.tier(size) }));
    }
    chooseOccasion(card) {
      const ids = card.dataset.occasionIds.split(',');
      const count = Number(card.dataset.occasionCount);
      if (this.busy || ids.length !== count || new Set(ids).size !== count || ids.some(id => !this.games.get(id)?.available)) return;
      this.selected = new Set(ids);
      this.setStatus(this.format(this.copy.occasionStatus, { count, occasion: card.dataset.occasionTitle }));
      this.render();
    }
    renderOccasions() {
      this.querySelectorAll('[data-bundle-size]').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.bundleSize) === this.bundleSize));
        button.disabled = Boolean(this.busy);
      });
      this.querySelectorAll('[data-occasion-group]').forEach(group => { group.hidden = Number(group.dataset.occasionGroup) !== this.bundleSize; });
      this.querySelectorAll('[data-occasion]').forEach(card => {
        const ids = card.dataset.occasionIds.split(',');
        const count = Number(card.dataset.occasionCount);
        const games = ids.map(id => this.games.get(id));
        const complete = ids.length === count && new Set(ids).size === count && games.every(Boolean);
        const available = complete && games.every(game => game.available);
        const selected = available && this.selected.size === count && ids.every(id => this.selected.has(id));
        const button = card.querySelector('[data-choose-occasion]');
        const values = { count, occasion: card.dataset.occasionTitle };
        button.disabled = this.busy || !available;
        button.setAttribute('aria-pressed', String(selected));
        button.textContent = !available ? this.copy.soldOut : selected ? this.copy.occasionSelected : this.format(this.copy.chooseOccasion, values);
        button.setAttribute('aria-label', this.format(selected ? this.copy.occasionSelectedNamed : this.copy.chooseOccasionNamed, values));
        card.classList.toggle('is-selected', selected);
        card.querySelector('.dob-occasion__price').hidden = !complete;
        if (complete) {
          card.querySelector('[data-occasion-original]').textContent = this.money(games.reduce((sum, game) => sum + game.price, 0));
          card.querySelector('[data-occasion-total]').textContent = this.money(this.discounted(games, this.tier(count)));
        }
      });
    }
    render() {
      const chosen = [...this.selected].map(id => this.games.get(id));
      const percent = this.tier(chosen.length);
      const original = chosen.reduce((sum, game) => sum + game.price, 0);
      const total = this.discounted(chosen, percent);
      const countLabel = chosen.length === 1 ? this.copy.oneGame : this.format(this.copy.games, { count: chosen.length });
      const tier = countLabel + (percent ? ' • ' + this.format(this.copy.off, { percent }) : '');
      this.querySelector('[data-builder-count]').textContent = tier;
      this.querySelectorAll('[data-builder-original],[data-tray-original]').forEach(node => { node.textContent = this.money(original); node.hidden = !percent; });
      this.querySelectorAll('[data-builder-total],[data-tray-total]').forEach(node => { node.textContent = this.money(total); });
      this.querySelector('[data-tray-tier]').textContent = tier;
      this.querySelectorAll('[data-game-price]').forEach(node => {
        const game = this.games.get(node.dataset.gamePrice);
        const incremental = game.available && !this.selected.has(game.id);
        const countWithGame = chosen.length + (incremental ? 1 : 0);
        const rate = game.available ? this.tier(countWithGame) : 0;
        const amount = incremental ? this.discounted([...chosen, game], rate) - total : this.discounted([game], rate);
        node.querySelector('[data-price-original]').hidden = amount >= game.price;
        node.querySelector('[data-price-current]').textContent = (incremental && amount >= 0 ? '+' : '') + this.money(amount);
        node.querySelector('[data-price-label]').textContent = incremental ? '' : (rate ? node.dataset.saleLabel : node.dataset.regularLabel) + ' ';
        node.querySelector('[data-price-note]').hidden = !incremental;
      });
      this.querySelectorAll('[data-tier-step]').forEach(node => {
        const reached = percent >= Number(node.dataset.tierStep);
        node.classList.toggle('is-active', reached);
        node.querySelector('.dob-tier-dot').textContent = reached ? '✓' : '';
        if (percent === Number(node.dataset.tierStep)) node.setAttribute('aria-current', 'step');
        else node.removeAttribute('aria-current');
      });
      const next = chosen.length === 2 ? 30 : 40;
      const message = chosen.length >= 4 ? this.copy.max : chosen.length < 2 ? this.copy.start : this.format(this.copy.next, { percent: next, count: chosen.length + 1 });
      this.querySelectorAll('[data-nudge]').forEach(nudge => {
        nudge.replaceChildren();
        message.split(/(\d+%)/).forEach(part => {
          if (/^\d+%$/.test(part)) { const accent = document.createElement('strong'); accent.className = 'dob-accent'; accent.textContent = part; nudge.append(accent); }
          else nudge.append(document.createTextNode(part));
        });
      });
      const tray = this.querySelector('[data-tray-games]'); tray.replaceChildren();
      chosen.forEach(game => {
        const item = document.createElement('button'); item.type = 'button'; item.className = 'dob-tray-game'; item.dataset.remove = game.id;
        item.setAttribute('aria-label', this.format(this.copy.removeGame, { game: game.title })); item.disabled = this.busy;
        item.append(this.pack(game)); const remove = document.createElement('span'); remove.textContent = '×'; remove.className = 'dob-tray-remove'; remove.setAttribute('aria-hidden', 'true'); item.append(remove); tray.append(item);
      });
      const add = document.createElement('button'); add.type = 'button'; add.className = 'dob-tray-add'; add.dataset.browse = ''; add.setAttribute('aria-label', this.copy.addGame); add.textContent = '+'; tray.append(add);
      this.querySelectorAll('[data-select]').forEach(button => {
        const game = this.games.get(button.dataset.select); const selected = this.selected.has(game.id);
        button.setAttribute('aria-pressed', String(selected)); button.disabled = this.busy || !game.available;
        const label = !game.available ? this.copy.soldOut : selected ? this.copy.selected : this.copy.add;
        const copy = button.querySelector('[data-select-copy]');
        if (copy) { copy.textContent = label; button.querySelector('[data-select-icon]').textContent = selected ? '−' : '+'; }
        else button.textContent = (selected ? '− ' : '+ ') + label;
        button.closest('.dsl-browse__card')?.classList.toggle('is-selected', selected);
        game.card.classList.toggle('is-selected', selected);
      });
      this.querySelectorAll('[data-add-selected]').forEach(button => { button.disabled = this.busy || !chosen.length; button.textContent = this.busy ? this.copy.adding : this.copy.addSelected + ' →'; });
      this.renderOccasions();
      if (this.galleryGame) this.updateGalleryButton();
    }
    buildBrowser() {
      const grid = this.querySelector('[data-browse-grid]'); grid.replaceChildren();
      this.games.forEach(game => {
        const card = document.createElement('article'); card.className = 'dsl-browse__card'; card.append(this.pack(game));
        const title = document.createElement('h3'); title.textContent = game.title; card.append(title);
        const price = document.createElement('p'); price.append(this.price(game)); card.append(price);
        const button = document.createElement('button'); button.type = 'button'; button.className = 'dsl-button dsl-button--lime'; button.dataset.select = game.id; card.append(button);
        grid.append(card);
      });
    }
    showGallery(id) {
      const game = this.games.get(id); if (!game) return;
      this.galleryGame = game; this.galleryRail.replaceChildren();
      this.querySelector('[data-gallery-title]').textContent = game.title;
      this.querySelector('[data-gallery-price]').replaceChildren(this.price(game));
      this.querySelector('[data-gallery-link]').href = game.url;
      game.images.forEach((photo, index) => {
        const frame = document.createElement('figure'); const image = document.createElement('img');
        image.src = photo.src; image.alt = photo.alt; image.width = photo.width; image.height = photo.height; image.loading = index ? 'lazy' : 'eager'; frame.append(image); this.galleryRail.append(frame);
      });
      this.updateGalleryButton(); this.gallery.showModal(); this.galleryRail.scrollLeft = 0; this.trackImages();
    }
    updateGalleryButton() {
      const button = this.querySelector('[data-gallery-select]'); const game = this.galleryGame;
      button.disabled = this.busy || !game.available;
      button.setAttribute('aria-pressed', String(this.selected.has(game.id)));
      button.textContent = !game.available ? this.copy.soldOut : this.selected.has(game.id) ? '− ' + this.copy.selected : '+ ' + this.copy.add;
    }
    trackImages() {
      const count = this.galleryRail.children.length;
      const current = count ? Math.round(this.galleryRail.scrollLeft / (this.galleryRail.clientWidth || 1)) + 1 : 0;
      this.querySelector('[data-image-counter]').textContent = current + ' / ' + count;
      this.querySelector('[data-image-previous]').disabled = current <= 1;
      this.querySelector('[data-image-next]').disabled = current >= count;
    }
    onClick(event) {
      const button = event.target.closest('button'); if (!button) return;
      if (button.hasAttribute('data-select')) this.toggle(button.dataset.select);
      else if (button.hasAttribute('data-bundle-size')) this.setBundleSize(Number(button.dataset.bundleSize));
      else if (button.hasAttribute('data-choose-occasion')) this.chooseOccasion(button.closest('[data-occasion]'));
      else if (button.hasAttribute('data-remove')) this.toggle(button.dataset.remove);
      else if (button.hasAttribute('data-inside')) this.showGallery(button.dataset.inside);
      else if (button.hasAttribute('data-browse')) this.browser.showModal();
      else if (button.hasAttribute('data-close')) button.closest('dialog').close();
      else if (button.hasAttribute('data-gallery-select')) { this.toggle(this.galleryGame.id); this.gallery.close(); }
      else if (button.hasAttribute('data-image-next') || button.hasAttribute('data-image-previous')) this.galleryRail.scrollBy({ left: this.galleryRail.clientWidth * (button.hasAttribute('data-image-next') ? 1 : -1), behavior: this.behavior() });
      else if (button.hasAttribute('data-add-selected')) this.addGames(button, [...this.selected]);
    }
    async addGames(button, ids) {
      if (this.busy || !ids.length || ids.some(id => !this.games.get(id)?.available)) return;
      if (!window.DODGameTiers) { this.setStatus(this.copy.error); return; }
      this.busy = true; this.setStatus(this.copy.adding); this.render();
      const root = window.Shopify?.routes?.root || '/';
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      const drawer = document.querySelector('cart-drawer');
      const returnFocus = button.closest('dialog') ? this.querySelector('.dob-tray [data-add-selected]') : button;
      drawer?.setActiveElement(returnFocus);
      const release = window.DODGameTiers.begin();
      let committed = false;
      try {
        const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: ids.map(id => ({ id: this.games.get(id).variant, quantity: 1 })), sections, sections_url: location.pathname }) });
        let cart = await response.json();
        if (!response.ok || cart.status || cart.errors) throw new Error(cart.description || cart.message || this.copy.error);
        committed = true;
        ids.forEach(id => this.selected.delete(id));
        cart = await window.DODGameTiers.sync(cart, sections);
        this.browser.close(); this.gallery.close();
        if (drawer && cart.sections && sections.every(id => cart.sections[id])) {
          drawer.classList.remove('is-empty'); drawer.renderContents(cart, false); drawer.open(returnFocus);
        } else { location.assign(root + 'cart'); }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dod-drinking-line', cartData: cart });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        this.setStatus(this.copy.added);
      } catch (error) {
        this.setStatus(committed ? this.copy.syncError : error.message || this.copy.error);
        if (committed) location.assign(root + 'cart');
      } finally { release(); this.busy = false; this.render(); }
    }
  }
  customElements.define('dod-drinking-line', DrinkingLine);
})();


